import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { api, setToken, getToken } from "./api";

const AuthCtx = createContext(null);

// Idle timeout: if the user hasn't interacted with the app for this many
// milliseconds, we sign them out and send them back to /login. This is on top
// of the JWT's own 24h expiry — it protects against a walked-away-from laptop.
const IDLE_TIMEOUT_MS = 30 * 60 * 1000;       // 30 minutes
const IDLE_CHECK_INTERVAL_MS = 30 * 1000;      // check every 30s
const LAST_ACTIVITY_KEY = "elle_last_activity";

function markActive() {
  try { localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now())); } catch {}
}

function readLastActivity() {
  try {
    const v = localStorage.getItem(LAST_ACTIVITY_KEY);
    return v ? parseInt(v, 10) : null;
  } catch { return null; }
}

export function AuthProvider({ children }) {
  const [author, setAuthor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [setupComplete, setSetupComplete] = useState(null);
  const idleTimerRef = useRef(null);

  const bootstrap = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/setup/status");
      setSetupComplete(data.setup_complete);
      if (data.setup_complete && getToken()) {
        // Check idle timeout before trusting the token.
        const last = readLastActivity();
        if (last && Date.now() - last > IDLE_TIMEOUT_MS) {
          setToken(null);
          localStorage.removeItem(LAST_ACTIVITY_KEY);
        } else {
          const me = await api.get("/auth/me");
          setAuthor(me.data);
          markActive();
        }
      }
    } catch (_e) {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { bootstrap(); }, []);

  // Track user activity on all common events. Throttled write (1/sec max).
  useEffect(() => {
    if (!author) return;
    let lastWrite = 0;
    const onActivity = () => {
      const now = Date.now();
      if (now - lastWrite > 1000) {
        markActive();
        lastWrite = now;
      }
    };
    const events = ["mousedown", "keydown", "scroll", "touchstart", "visibilitychange"];
    events.forEach(ev => window.addEventListener(ev, onActivity, { passive: true }));
    return () => events.forEach(ev => window.removeEventListener(ev, onActivity));
  }, [author]);

  // Periodic idle check — if the last-activity stamp is stale, log out.
  useEffect(() => {
    if (!author) return;
    idleTimerRef.current = setInterval(() => {
      const last = readLastActivity();
      if (!last || Date.now() - last > IDLE_TIMEOUT_MS) {
        // Sign out this tab. We use setToken(null) + hard redirect so any in-flight
        // API calls don't race with a stale token.
        try { localStorage.removeItem(LAST_ACTIVITY_KEY); } catch {}
        setToken(null);
        setAuthor(null);
        if (window.location.pathname.startsWith("/dashboard")) {
          window.location.href = "/login?reason=idle";
        }
      }
    }, IDLE_CHECK_INTERVAL_MS);
    return () => { if (idleTimerRef.current) clearInterval(idleTimerRef.current); };
  }, [author]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setToken(data.token);
    markActive();
    setAuthor(data.author);
    return data;
  };

  const setup = async (body) => {
    const { data } = await api.post("/auth/setup", body);
    setToken(data.token);
    markActive();
    setAuthor(data.author);
    setSetupComplete(true);
    return data;
  };

  const logout = () => {
    setToken(null);
    try { localStorage.removeItem(LAST_ACTIVITY_KEY); } catch {}
    setAuthor(null);
    window.location.href = "/login";
  };

  return (
    <AuthCtx.Provider value={{ author, loading, setupComplete, login, setup, logout, refresh: bootstrap }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
